<template>
  <Modal :is-open="isOpen" :title="title" size="md" @close="emitClose">
    <LoaderIndicator v-if="isLoading" with-container />

    <ModalContent
      v-else-if="isHomeBrokerDisabled"
      image-src="/assets/images/pages/trade/request_broker.png"
      image-alt="Imagem com fundo azul, com um monitor de um computador acessando o home broker da Warren e ao lado direto dele o texto Warren Home Broker"
      title="Deseja solicitar o Home Broker?"
      description="Ao aceitar, você poderá investir do seu jeito na Bolsa. Ele estará ativo em alguns minutos."
    />
    <ModalStatus v-else v-bind="modalStatusProps" />
    <ButtonContainer>
      <template #button-primary>
        <ButtonSecondary
          v-if="isHomeBrokerDisabled"
          label="Cancelar"
          @click="handleCancelClick"
        />
        <ButtonSecondary v-else label="Ok" @click="closeBrokerModal" />
      </template>
      <template v-if="isHomeBrokerDisabled" #button-secondary>
        <ButtonSecondary label="Solicitar" @click="requestBroker" />
      </template>
    </ButtonContainer>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import {
  Modal,
  ButtonContainer,
  ButtonSecondary,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { ModalContent, ModalStatus } from './components';

import { requestHomeBrokerAccess } from '@/modules/trade/services/home-broker';
import { TradeHomeBrokerStatus } from '@/modules/trade/services/account';
import {
  trackRequestHomeBrokerClick,
  trackCancelHomeBrokerClick
} from '@/modules/trade/views/TradeView/trackers';

enum BrokerSteps {
  request = 1,
  status = 2
}

interface Emits {
  (event: 'close'): void;
  (event: 'update-status'): void;
}

const emits = defineEmits<Emits>();

interface Props {
  isOpen: boolean;
  brokerStatus: string;
}

const props = defineProps<Props>();

const isLoading = ref(false);
const step = ref(BrokerSteps.request);

const title = computed(() => {
  return props.brokerStatus === TradeHomeBrokerStatus.Requested
    ? 'Home Broker Solicitado'
    : 'Solicitar Home Broker';
});

const isHomeBrokerDisabled = computed(() => {
  return (
    step.value === BrokerSteps.request &&
    props.brokerStatus === TradeHomeBrokerStatus.Disabled
  );
});

const isBrokerStatusRequested = computed(() => {
  return props.brokerStatus === TradeHomeBrokerStatus.Requested;
});

const modalStatusProps = computed(() => {
  const infoProps = {
    status: 'information',
    title: 'Quase lá! Estamos preparando o Home Broker para você',
    description: 'Aguarde alguns minutos para começar a investir.'
  };
  const successProps = {
    status: 'positive',
    title: 'O Home Broker foi solicitado',
    description: 'Aguarde alguns minutos para começar a investir.'
  };

  return isBrokerStatusRequested.value ? infoProps : successProps;
});

function emitClose() {
  emits('close');
}

function emitUpdateStatus() {
  emits('update-status');
}

function handleCancelClick() {
  trackCancelHomeBrokerClick();
  emitClose();
}

function closeBrokerModal() {
  if (isBrokerStatusRequested.value) emitClose();
  else emitUpdateStatus();
}

async function requestBroker() {
  trackRequestHomeBrokerClick();
  isLoading.value = true;

  try {
    await requestHomeBrokerAccess();
  } finally {
    isLoading.value = false;
    step.value = BrokerSteps.status;
  }
}
</script>

<style lang="less" scoped>
:deep(.modal-content) {
  padding: 0;
}
</style>
